/**
 * "Swap prompt group" on a texts-not-filled error (user, 21/10/2026: "I
 * don't want to edit the prompt group. I wanted to edit the creative groups
 * for the container — a swap to let me swap a prompt group and generate
 * again"): what the container writes with now (the failing row marked),
 * the editor's own pick row for what replaces it, where it lands — this
 * container only, or its master and every container that follows it — and
 * one button that swaps and fills the texts again.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode, useState } from 'react';
import { ArrowLeftRight, X } from 'lucide-react';
import type { DropGenPromptSwapScope, DropGenPromptSwapView } from '@trafficpulse/shared';

import { Button } from '@/components/ui';
import { useTextPromptGroups } from '@/features/campaign-texts/hooks/useTextPromptGroups';
import { getRandomizeScopeFolderOptions, type TreeGroupLike } from '@/features/campaign-texts/utils/group-tree.utils';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';

import { PickRow, type PromptPick } from '../../../editor/creative/picks';
import { ChoiceChips } from '../../../editor/general/controls/ChoiceChips';
import { isFailing, pickLabel, picksOf, swapBody } from './swap-model';
import { usePromptSwap, usePromptSwapView } from './use-prompt-swap';

const Row: FC<{ label: string; children: ReactNode; note?: string }> = ({ label, children, note }) => (
  <div className="grid gap-1.5 sm:grid-cols-[150px_minmax(0,1fr)] sm:items-start">
    <span className="pt-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">{children}</div>
      {note ? <p className="text-[11px] text-muted-foreground">{note}</p> : null}
    </div>
  </div>
);

const NowRow: FC<{ view: DropGenPromptSwapView; groups: TreeGroupLike[] }> = ({ view, groups }) => (
  <Row label="Writes its texts with">
    {picksOf(view, groups).map((p) => (
      <span key={p.folderId} data-testid="swap-now" className={cn('inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-[12px]', isFailing(p, view) ? 'border-destructive/40 bg-destructive/10 text-destructive' : 'border-border bg-muted/40')}>
        {pickLabel(p, groups)}
        {isFailing(p, view) ? <span className="text-[10px] font-semibold uppercase">failed</span> : null}
      </span>
    ))}
  </Row>
);

const scopeOptions = (view: DropGenPromptSwapView) => [
  { value: 'container' as const, label: `Only ${view.container?.name ?? 'this container'}` },
  { value: 'master' as const, label: `The master “${view.master?.name}” · ${view.master?.followers} container${view.master?.followers === 1 ? '' : 's'}` },
];

const scopeNote = (view: DropGenPromptSwapView, scope: DropGenPromptSwapScope) =>
  scope === 'master'
    ? `Every container that follows ${view.master?.name}'s texts writes with the new group.`
    : `${view.container?.name} gets its own texts settings — a copy of ${view.master?.name}'s, with this swap. The other containers keep the master's.`;

const Body: FC<{ view: DropGenPromptSwapView; groups: TreeGroupLike[]; countryKey: string; onDone: () => void }> = ({ view, groups, countryKey, onDone }) => {
  const [next, setNext] = useState<PromptPick>({ folderId: '', ruleIds: [] });
  const [scope, setScope] = useState<DropGenPromptSwapScope>('container');
  const swap = usePromptSwap(countryKey, view.errorId);
  const { success, error, warning } = useToast();
  const body = swapBody(view, groups, next);
  const run = async () => {
    if (!body) return;
    try {
      const out = await swap.mutateAsync({ ...body, scope: view.master ? scope : 'container' });
      if (out.fillError) warning('Swapped — the fill did not start', `${getApiErrorMessage(out.fillError)} — press Fill texts again.`);
      else if (out.queued) success('Swapped — the texts are filled again when the running fill ends', 'The running fill uses the old group; the next one, with the new group, starts by itself.');
      else success('Swapped — filling the texts again', 'The error turns Resolved once its drafts have their texts.');
      onDone();
    } catch (err) {
      error('Could not swap the prompt group', getApiErrorMessage(err));
    }
  };
  return (
    <>
      <NowRow view={view} groups={groups} />
      <Row label="Replace it with">
        <div className="w-full max-w-xl" data-testid="swap-pick"><PickRow groups={groups} folders={getRandomizeScopeFolderOptions(groups)} pick={next} onChange={setNext} /></div>
      </Row>
      {view.master ? (
        <Row label="Swap on" note={scopeNote(view, scope)}>
          <ChoiceChips options={scopeOptions(view)} value={scope} onChange={setScope} ariaLabel="Where the swap lands" />
        </Row>
      ) : null}
      {view.fillRunning ? (
        <p className="text-[11px] text-warning" data-testid="swap-filling">A fill is running now — it writes with the groups as they were. After the swap, the texts are filled again with the new group as soon as it ends.</p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onDone}>Cancel</Button>
        <Button size="sm" onClick={() => void run()} disabled={!body || swap.isPending} data-testid="swap-run">
          <ArrowLeftRight className="mr-1.5 h-3.5 w-3.5" aria-hidden />
          {swap.isPending ? 'Swapping…' : 'Swap and fill again'}
        </Button>
      </div>
    </>
  );
};

export const SwapPanel: FC<{ countryKey: string; errorId: string; onClose: () => void }> = ({ countryKey, errorId, onClose }) => {
  const view = usePromptSwapView(countryKey, errorId, true);
  const groups = useTextPromptGroups();
  const v = view.data;
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-primary/25 bg-background/60 p-3 duration-200 animate-in fade-in-0 slide-in-from-top-1 motion-reduce:animate-none" data-testid="swap-panel">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-[13px] font-semibold">
          <ArrowLeftRight className="h-4 w-4 text-primary" aria-hidden />
          Swap the prompt group{v?.container ? ` · ${v.container.name}` : ''}
        </span>
        <button type="button" aria-label="Close the swap" onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted"><X className="h-3.5 w-3.5" /></button>
      </div>
      {view.isLoading || groups.isLoading ? <p className="text-[12px] text-muted-foreground">Reading the container's prompt groups…</p> : null}
      {view.isError ? <p className="text-[12px] text-destructive">{getApiErrorMessage(view.error)}</p> : null}
      {v && !v.container ? <p className="text-[12px] text-muted-foreground">No swap here — {v.why}.</p> : null}
      {v?.container && groups.data ? <Body view={v} groups={groups.data} countryKey={countryKey} onDone={onClose} /> : null}
    </div>
  );
};
